import {
  buildSemanticGroups,
  buildSemanticLights,
  buildSceneJson,
} from "./renderPipeline";

// Viewport.jsx's free-fly camera has a fixed fov (never reassigned after
// construction) — mirrored here rather than plumbing a new imperative
// accessor through Viewport, since a piloted camera node's own `fov` is
// already sitting right there in `nodes`.
const FREE_CAMERA_FOV = 50;

// Live, read-only preview of the exact scene.json the AI render pipeline
// builds (see buildSceneJson in renderPipeline.js) — recomputed from
// `nodes`/`sceneSettings` on every render, so editing a description or
// reference image is reflected immediately. Deliberately the *pre-render*
// shape: at generation time, buildImageManifest (aiRender.js) swaps the
// beauty render in as "@image1" and every reference URL here for "@image2"
// onward, but that only makes sense once a beauty render actually exists —
// nothing to preview here without one. `environmentInfo` (the "environment"
// key — ambient fill + every light's color/intensity/relative position) is
// computed by Viewport.jsx (buildEnvironmentInfo, renderPipeline.js) rather
// than here, unlike everything else on this page — it needs the live THREE
// scene graph and a real camera's world matrix, neither of which this
// component has access to.
export default function InspectPanel({
  nodes,
  sceneSettings,
  activeCameraId,
  objectBoundsById,
  lightBoundsById,
  environmentInfo,
}) {
  const activeCameraNode = activeCameraId
    ? nodes.find((n) => n._id === activeCameraId)
    : null;
  const groups = buildSemanticGroups(nodes);
  // Keyed by id rather than trusting index alignment — this is a *different*
  // `groups` array (a fresh call, from this component's own `nodes`) than
  // whichever one Viewport.jsx used to produce `objectBoundsById`; see
  // computeObjectBoundsById's own comment in renderPipeline.js.
  const objectBounds = groups.map(
    (g) => objectBoundsById?.[g.root._id] ?? null,
  );
  const lights = buildSemanticLights(nodes);
  const lightBounds = lights.map((n) => lightBoundsById?.[n._id] ?? null);
  const sceneJson = buildSceneJson({
    sceneSettings,
    sourceCamera: { fov: activeCameraNode?.fov ?? FREE_CAMERA_FOV },
    groups,
    objectBounds,
    lights,
    lightBounds,
    environment: environmentInfo || undefined,
  });

  return (
    <div
      id="Scene3DInspectPanel"
      className="flex-1 min-h-0 p-3 overflow-y-auto">
      <pre className="text-[10px] leading-tight font-mono whitespace-pre-wrap">
        {JSON.stringify(sceneJson, null, 2)}
      </pre>
    </div>
  );
}
